import React, { useState, useEffect, useCallback } from "react";
import { Link } from "react-router-dom";
import LeadModal from "../components/LeadModal";
import {
  Sparkle, ArrowRight, CheckCircle, Buildings, Users, UserCircle,
  Megaphone, Stack, ClipboardText, ChartLineUp, ShieldCheck, IdentificationBadge,
  Play, List, X, Palette, Globe, EnvelopeSimple,
  ArrowUpRight, Quotes, ChatCircleText, Table, ClipboardText as ClipIcon,
  FolderSimple, CurrencyDollarSimple, Warning, PaperPlaneTilt, Handshake,
  Lightning, ShieldStar, ArrowDown, Cardholder,
} from "@phosphor-icons/react";

// ---------- Small helpers (preserved) ----------
const Section = ({ id, children, className = "" }) => (
  <section id={id} className={`max-w-7xl mx-auto px-5 sm:px-8 lg:px-10 ${className}`}>
    {children}
  </section>
);

const Eyebrow = ({ children }) => (
  <div className="text-[11px] tracking-[0.22em] uppercase font-bold text-[#A3E635] flex items-center gap-2">
    <Sparkle size={12} weight="fill" /> {children}
  </div>
);

const GradientHead = ({ children, className = "" }) => (
  <h2 className={`text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-white leading-[1.05] ${className}`}>
    {children}
  </h2>
);

// ---------- Top Nav ----------
function TopNav({ openLead }) {
  const [open, setOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);
  // Hidden admin shortcut: sequence "adm" opens /admin-login
  useEffect(() => {
    let buf = "";
    const onKey = (e) => {
      if (e.target?.tagName === "INPUT" || e.target?.tagName === "TEXTAREA") return;
      buf = (buf + (e.key || "").toLowerCase()).slice(-3);
      if (buf === "adm") window.location.href = "/admin-login";
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, []);

  // Nav aligned to the new positioning: Solutions | Platform | Customers | Pricing | Resources | About
  const links = [
    { href: "#solutions", label: "Solutions" },
    { href: "#platform",  label: "Platform" },
    { href: "#customers", label: "Customers" },
    { href: "#pricing",   label: "Pricing" },
    { href: "#resources", label: "Resources" },
    { href: "#about",     label: "About" },
  ];
  return (
    <div className={`fixed top-0 inset-x-0 z-40 transition-all ${scrolled ? "backdrop-blur-md bg-[#0B0910]/80 border-b border-white/5" : ""}`}>
      <Section className="flex items-center justify-between h-16">
        <a href="#top" className="flex items-center gap-2 group" data-testid="nav-logo">
          <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#8B5CF6] to-[#EC4899] grid place-items-center">
            <Sparkle size={16} weight="fill" color="white" />
          </div>
          <div className="text-white font-black tracking-tight">Crew<span className="text-[#22D3EE]">HQ</span></div>
        </a>
        <nav className="hidden lg:flex items-center gap-7">
          {links.map(l => (
            <a key={l.href} href={l.href} className="text-sm text-muted hover:text-white transition" data-testid={`nav-${l.label.toLowerCase()}`}>{l.label}</a>
          ))}
        </nav>
        {/* Public "Sign in / Start free" CTAs are intentionally hidden from the marketing site.
            Existing /login, /get-started and /admin-login routes still work for internal use. */}
        <div className="hidden lg:flex items-center gap-2">
          <button onClick={() => openLead("landing_nav")} data-testid="nav-talk-to-sales"
                  className="btn-primary text-sm flex items-center gap-1.5">
            Talk to Sales <ArrowRight size={12} weight="bold" />
          </button>
        </div>
        <button className="lg:hidden text-white p-2" onClick={() => setOpen(!open)} aria-label="Menu" data-testid="nav-menu">
          {open ? <X size={20} /> : <List size={20} />}
        </button>
      </Section>
      {open && (
        <div className="lg:hidden border-t border-white/5 bg-[#0B0910]/95 backdrop-blur-md">
          <Section className="py-4 flex flex-col gap-3">
            {links.map(l => <a key={l.href} onClick={() => setOpen(false)} href={l.href} className="text-white text-sm py-1">{l.label}</a>)}
            <div className="pt-2 border-t border-white/5">
              <button onClick={() => { setOpen(false); openLead("landing_nav_mobile"); }}
                      className="btn-primary text-sm w-full text-center">
                Talk to Sales
              </button>
            </div>
          </Section>
        </div>
      )}
    </div>
  );
}

// ---------- Hero ----------
function Hero({ openLead }) {
  return (
    <div id="top" className="relative pt-32 pb-20 sm:pt-40 sm:pb-28 overflow-hidden">
      <div className="absolute inset-0 -z-10">
        <div className="absolute -top-40 left-1/2 -translate-x-1/2 w-[900px] h-[900px] rounded-full bg-gradient-to-br from-[#8B5CF6]/25 via-[#EC4899]/10 to-transparent blur-3xl" />
        <div className="absolute inset-0" style={{
          backgroundImage: "radial-gradient(rgba(255,255,255,0.04) 1px, transparent 1px)",
          backgroundSize: "24px 24px",
        }} />
      </div>
      <Section className="text-center">
        <Eyebrow>Creator Operations Platform</Eyebrow>
        <h1 className="mt-5 text-4xl sm:text-6xl lg:text-7xl font-black tracking-[-0.02em] text-white leading-[1.02]" data-testid="hero-headline">
          The Operating System for{" "}
          <span className="bg-gradient-to-r from-[#22D3EE] via-[#A3E635] to-[#EC4899] bg-clip-text text-transparent">
            Creator Operations
          </span>
          .
        </h1>
        <p className="mt-6 max-w-3xl mx-auto text-base sm:text-lg text-muted leading-relaxed">
          Run your entire creator operation from one platform. Onboard creators, manage campaigns,
          review submissions, approve content, automate workflows and manage payouts.
          Scale operations without spreadsheets, WhatsApp or disconnected tools.
        </p>
        <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
          <button onClick={() => openLead("landing_hero")} className="btn-primary text-base flex items-center justify-center gap-2 px-6 py-3" data-testid="hero-cta-talk-to-sales">
            Talk to Sales <ArrowRight size={14} weight="bold" />
          </button>
          <button onClick={() => openLead("landing_hero_demo")} className="btn-ghost text-base flex items-center justify-center gap-2 px-6 py-3" data-testid="hero-cta-book-demo">
            <Play size={14} weight="fill" /> Book Demo
          </button>
        </div>
        <div className="mt-12 flex flex-wrap justify-center gap-x-6 gap-y-2 text-xs text-muted">
          {["Onboarding", "Campaigns", "Submissions", "Approvals", "Payouts", "Reporting"].map(c => (
            <span key={c} className="flex items-center gap-1.5"><CheckCircle size={12} weight="fill" className="text-[#A3E635]" /> {c}</span>
          ))}
        </div>
      </Section>
    </div>
  );
}

// ---------- Section 2: The Problem ----------
function Problem() {
  const chaosStack = [
    { icon: ChatCircleText,  label: "WhatsApp" },
    { icon: Table,           label: "Google Sheets" },
    { icon: ClipIcon,        label: "Forms" },
    { icon: EnvelopeSimple,  label: "Emails" },
    { icon: FolderSimple,    label: "Google Drive" },
    { icon: CurrencyDollarSimple, label: "Finance handoffs" },
    { icon: Warning,         label: "Chaos" },
  ];
  return (
    <div id="problem" className="py-24 border-t border-white/5 relative overflow-hidden">
      <div className="absolute inset-0 -z-10">
        <div className="absolute -top-32 right-0 w-[500px] h-[500px] rounded-full bg-gradient-to-br from-[#EC4899]/10 to-transparent blur-3xl" />
      </div>
      <Section>
        <div className="max-w-3xl">
          <Eyebrow>The problem</Eyebrow>
          <GradientHead className="mt-3">
            Creator programs don&apos;t fail because of marketing.
            <br />
            <span className="bg-gradient-to-r from-[#EC4899] to-[#8B5CF6] bg-clip-text text-transparent">They fail because operations don&apos;t scale.</span>
          </GradientHead>
          <p className="mt-5 text-muted text-base leading-relaxed">
            Your team already has creators. The real problem is stitching a workflow across seven
            disconnected tools every single week — and watching it break every time you double your roster.
          </p>
        </div>

        <div className="mt-14 grid lg:grid-cols-2 gap-8 items-center">
          {/* Left: chaos stack */}
          <div className="glass p-6 sm:p-8" data-testid="problem-chaos">
            <div className="text-[10px] uppercase tracking-[0.22em] text-muted font-bold mb-4">Today · fragmented</div>
            <div className="space-y-2">
              {chaosStack.map((s, i) => {
                const Icon = s.icon;
                const isLast = i === chaosStack.length - 1;
                return (
                  <React.Fragment key={s.label}>
                    <div className={`flex items-center gap-3 p-3 rounded-xl border ${isLast
                      ? "border-[#EC4899]/40 bg-[#EC4899]/10"
                      : "border-white/10 bg-white/5"}`}>
                      <div className={`w-8 h-8 rounded-lg grid place-items-center ${isLast
                        ? "bg-[#EC4899] text-white"
                        : "bg-white/10 text-white"}`}>
                        <Icon size={14} weight="bold" />
                      </div>
                      <div className={`text-sm font-bold ${isLast ? "text-[#EC4899]" : "text-white"}`}>{s.label}</div>
                    </div>
                    {!isLast && <div className="flex justify-center opacity-60"><ArrowDown size={12} weight="bold" className="text-muted" /></div>}
                  </React.Fragment>
                );
              })}
            </div>
          </div>

          {/* Right: CrewHQ */}
          <div className="glass p-6 sm:p-8 relative overflow-hidden" data-testid="problem-crewhq">
            <div className="absolute -top-16 -right-16 w-64 h-64 rounded-full bg-gradient-to-br from-[#22D3EE]/25 to-[#A3E635]/10 blur-3xl" />
            <div className="relative">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#A3E635] font-bold mb-4">Tomorrow · centralised</div>
              <div className="p-5 rounded-2xl border border-[#22D3EE]/40 bg-gradient-to-br from-[#22D3EE]/10 to-[#8B5CF6]/10">
                <div className="flex items-center gap-3">
                  <div className="w-10 h-10 rounded-xl bg-gradient-to-br from-[#22D3EE] to-[#8B5CF6] grid place-items-center">
                    <Sparkle size={16} weight="fill" color="white" />
                  </div>
                  <div>
                    <div className="text-white font-black">CrewHQ</div>
                    <div className="text-[11px] text-muted">One workspace · one workflow · one source of truth</div>
                  </div>
                </div>
                <div className="mt-6 flex flex-wrap gap-2">
                  {["Onboard", "Verify", "Assign", "Communicate", "Review", "Approve", "Pay", "Report"].map(t => (
                    <span key={t} className="pill text-[10px]" style={{
                      color: "#22D3EE", background: "#22D3EE1a", borderColor: "#22D3EE55",
                    }}>{t}</span>
                  ))}
                </div>
              </div>
              <p className="mt-6 text-sm text-muted leading-relaxed">
                CrewHQ replaces every disconnected tool with a single operational workspace.
                Your team stops chasing files, screenshots and payment threads — and starts running the program.
              </p>
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
}

// ---------- Section 3: Creator Lifecycle ----------
function Lifecycle() {
  const steps = [
    { icon: PaperPlaneTilt,       label: "Invite" },
    { icon: UserCircle,           label: "Onboard" },
    { icon: ClipboardText,        label: "Collect Info" },
    { icon: IdentificationBadge,  label: "Verify" },
    { icon: Handshake,            label: "Agreement" },
    { icon: Stack,                label: "Campaign Assignment" },
    { icon: ClipIcon,             label: "Brief Sharing" },
    { icon: ChatCircleText,       label: "Communication" },
    { icon: Megaphone,            label: "Submission" },
    { icon: CheckCircle,          label: "Review" },
    { icon: ShieldCheck,          label: "Approval" },
    { icon: CurrencyDollarSimple, label: "Payments" },
    { icon: ChartLineUp,          label: "Reporting" },
  ];
  return (
    <div id="platform" className="py-24 border-t border-white/5">
      <Section>
        <div className="max-w-3xl">
          <Eyebrow>Creator lifecycle · fully covered</Eyebrow>
          <GradientHead className="mt-3">Every step of the creator relationship — inside CrewHQ.</GradientHead>
          <p className="mt-4 text-muted">
            From the first invite to the final payout and post-campaign report, every workflow lives inside one platform.
            No handoff to another tool. No data lost between steps.
          </p>
        </div>

        <div className="mt-12 relative">
          {/* Timeline rail */}
          <div className="hidden lg:block absolute left-0 right-0 top-8 h-px bg-gradient-to-r from-[#22D3EE]/10 via-[#8B5CF6]/40 to-[#EC4899]/10" />
          <div className="grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-7 gap-3">
            {steps.slice(0, 7).map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={s.label} className="glass p-4 text-center relative hover-lift" data-testid={`lifecycle-${i}`}>
                  <div className="mx-auto w-11 h-11 rounded-xl bg-gradient-to-br from-[#22D3EE] to-[#8B5CF6] grid place-items-center">
                    <Icon size={18} weight="bold" color="white" />
                  </div>
                  <div className="mt-3 text-[10px] uppercase tracking-wider text-muted font-bold">Step {i + 1}</div>
                  <div className="mt-1 text-sm font-bold text-white">{s.label}</div>
                </div>
              );
            })}
          </div>
          <div className="mt-3 grid grid-cols-2 sm:grid-cols-3 lg:grid-cols-6 gap-3">
            {steps.slice(7).map((s, i) => {
              const Icon = s.icon;
              return (
                <div key={s.label} className="glass p-4 text-center hover-lift" data-testid={`lifecycle-${i + 7}`}>
                  <div className="mx-auto w-11 h-11 rounded-xl bg-gradient-to-br from-[#8B5CF6] to-[#EC4899] grid place-items-center">
                    <Icon size={18} weight="bold" color="white" />
                  </div>
                  <div className="mt-3 text-[10px] uppercase tracking-wider text-muted font-bold">Step {i + 8}</div>
                  <div className="mt-1 text-sm font-bold text-white">{s.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </Section>
    </div>
  );
}

// ---------- Section 4: Core Platform ----------
function CorePlatform() {
  const systems = [
    { icon: UserCircle,       title: "Creator Operations",   body: "Single source of truth for every creator — profile, verification, agreements, history." },
    { icon: Stack,            title: "Campaign Operations",  body: "Structured briefs, tasks, deadlines and evidence rules — for every campaign, at any scale." },
    { icon: Lightning,        title: "Workflow Automation",  body: "Trigger reminders, status changes, hand-offs and notifications — without human babysitting." },
    { icon: Megaphone,        title: "Submission Management", body: "Every deliverable in one queue with structured metadata, previews and status." },
    { icon: ShieldCheck,      title: "Approvals",            body: "Multi-step review with audit trails — brand, agency, admin sign-offs before anything ships." },
    { icon: ClipboardText,    title: "Reporting",            body: "Project, creator, agency and campaign roll-ups. Excel and CSV exports on demand." },
    { icon: ChartLineUp,      title: "Analytics",            body: "Reach, spend, throughput and operational KPIs your leadership actually asks about." },
    { icon: CurrencyDollarSimple, title: "Payments",         body: "Track earnings, gate payouts on approval, and export finance-ready payout files." },
    { icon: Users,            title: "Role Management",      body: "Strict multi-tenant scopes — admin, brand, agency, creator — with no data leakage." },
  ];
  return (
    <div className="py-24 border-t border-white/5">
      <Section>
        <div className="max-w-3xl">
          <Eyebrow>The platform · grouped by operations, not features</Eyebrow>
          <GradientHead className="mt-3">Nine operational systems. One platform.</GradientHead>
          <p className="mt-4 text-muted">
            Every function your ops team already runs — just centralised, structured and connected.
            No isolated feature dumps. Everything belongs to a system, and every system talks to the next.
          </p>
        </div>
        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-3 gap-3">
          {systems.map((s, i) => {
            const Icon = s.icon;
            return (
              <div key={i} className="glass p-5 group hover-lift" data-testid={`system-${i}`}>
                <div className="w-10 h-10 rounded-xl bg-white/5 border border-white/10 grid place-items-center group-hover:border-[#22D3EE]/40 transition">
                  <Icon size={18} weight="bold" className="text-[#22D3EE]" />
                </div>
                <div className="mt-4 text-white font-bold">{s.title}</div>
                <div className="mt-1 text-xs text-muted leading-relaxed">{s.body}</div>
              </div>
            );
          })}
        </div>
      </Section>
    </div>
  );
}

// ---------- Section 5: Solutions (entry points, kept per user request) ----------
function SolutionCard({ icon: Icon, gradient, badge, title, body, cta, onClick, testid }) {
  return (
    <div className="glass p-6 lg:p-7 relative overflow-hidden group hover-lift" data-testid={testid}>
      <div className={`absolute -top-16 -right-16 w-48 h-48 rounded-full bg-gradient-to-br ${gradient} opacity-30 blur-3xl group-hover:opacity-60 transition`} />
      <div className="relative">
        <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${gradient} grid place-items-center`}>
          <Icon size={20} weight="bold" color="white" />
        </div>
        <div className="mt-4 text-[10px] uppercase tracking-[0.2em] font-bold text-muted">{badge}</div>
        <h3 className="mt-1 text-xl font-bold text-white">{title}</h3>
        <p className="mt-2 text-sm text-muted leading-relaxed">{body}</p>
        <button onClick={onClick} className="mt-5 inline-flex items-center gap-1.5 text-sm font-bold text-white group-hover:text-[#22D3EE] transition" data-testid={`${testid}-cta`}>
          {cta} <ArrowUpRight size={14} weight="bold" />
        </button>
      </div>
    </div>
  );
}

function Solutions({ openLead }) {
  return (
    <div id="solutions" className="py-24 border-t border-white/5">
      <Section>
        <div className="max-w-3xl">
          <Eyebrow>Solutions · built for your team</Eyebrow>
          <GradientHead className="mt-3">One platform, three ways to run creator operations.</GradientHead>
          <p className="mt-4 text-muted text-base">
            The same operational backbone — configured for Brands, Agencies and in-house Creator Teams.
          </p>
        </div>
        <div className="mt-10 grid lg:grid-cols-2 gap-4">
          <SolutionCard
            icon={Buildings}
            gradient="from-[#22D3EE] to-[#8B5CF6]"
            badge="For Brands"
            title="Run your creator operations in-house"
            body="Onboard your ambassadors, run campaigns, review submissions, approve content and manage payouts — from one control room. Zero spreadsheets, zero WhatsApp threads."
            cta="Talk to Sales"
            onClick={() => openLead("solutions_brand")}
            testid="sol-brands"
          />
          <SolutionCard
            icon={Users}
            gradient="from-[#EC4899] to-[#8B5CF6]"
            badge="For Agencies"
            title="Operate creator programs for every client you serve"
            body="Manage rosters across client brands, ship briefs, review across a single queue, and roll up finance-ready reports per client. White-label the entire experience under your agency identity."
            cta="Talk to Sales"
            onClick={() => openLead("solutions_agency")}
            testid="sol-agencies"
          />
        </div>

        {/* Creator entry point — public users go through the sales team; internal creators use /login/creator */}
        <div className="mt-6 glass p-5 lg:p-6 flex flex-col sm:flex-row items-start sm:items-center gap-4" data-testid="sol-creators">
          <div className="w-11 h-11 rounded-xl bg-gradient-to-br from-[#A3E635] to-[#22D3EE] grid place-items-center flex-shrink-0">
            <UserCircle size={20} weight="bold" color="white" />
          </div>
          <div className="flex-1 min-w-0">
            <div className="text-[10px] uppercase tracking-[0.2em] font-bold text-muted">For Creator Teams</div>
            <div className="text-white text-sm font-bold mt-0.5">
              In-house creator marketing or community team? Bring us your workflow — we&apos;ll show you how CrewHQ replaces every tool you&apos;re juggling.
            </div>
          </div>
          <button onClick={() => openLead("solutions_creator_team")} className="btn-ghost text-sm flex items-center gap-1.5 whitespace-nowrap" data-testid="sol-creators-cta">
            Talk to Sales <ArrowRight size={12} weight="bold" />
          </button>
        </div>
      </Section>
    </div>
  );
}

// ---------- Section 6: Who it's for ----------
function WhoItsFor() {
  const cards = [
    { icon: Buildings, title: "Brands",
      body: "Consumer, D2C, edtech, wellness, sports — any brand running an ambassador or creator program.",
      accent: "from-[#22D3EE] to-[#8B5CF6]" },
    { icon: Users, title: "Agencies",
      body: "Talent, influencer, activation and campus agencies executing creator programs on behalf of client brands.",
      accent: "from-[#EC4899] to-[#8B5CF6]" },
    { icon: ShieldStar, title: "Enterprise Creator Teams",
      body: "In-house creator marketing, community and social teams operating creator programs at 1,000+ creator scale.",
      accent: "from-[#A3E635] to-[#22D3EE]" },
  ];
  return (
    <div id="customers" className="py-24 border-t border-white/5">
      <Section>
        <div className="max-w-3xl">
          <Eyebrow>Who buys CrewHQ</Eyebrow>
          <GradientHead className="mt-3">Built for the teams already running creator programs.</GradientHead>
          <p className="mt-4 text-muted">
            If your team already coordinates 20 to 20,000 creators — CrewHQ is your operational backbone.
          </p>
        </div>
        <div className="mt-10 grid md:grid-cols-3 gap-3">
          {cards.map((c, i) => {
            const Icon = c.icon;
            return (
              <div key={i} className="glass p-6 hover-lift" data-testid={`who-${i}`}>
                <div className={`w-11 h-11 rounded-xl bg-gradient-to-br ${c.accent} grid place-items-center`}>
                  <Icon size={20} weight="bold" color="white" />
                </div>
                <div className="mt-4 text-white text-xl font-black">{c.title}</div>
                <div className="mt-2 text-sm text-muted leading-relaxed">{c.body}</div>
              </div>
            );
          })}
        </div>
      </Section>
    </div>
  );
}

// ---------- Section 7: White label ----------
function WhiteLabel({ openLead }) {
  const items = [
    { icon: Sparkle,        title: "Your Logo" },
    { icon: Palette,        title: "Your Colors" },
    { icon: Globe,          title: "Your Domain" },
    { icon: EnvelopeSimple, title: "Your Emails" },
    { icon: UserCircle,     title: "Your Creator Experience" },
  ];
  return (
    <div className="py-24 border-t border-white/5">
      <Section className="grid lg:grid-cols-2 gap-10 items-center">
        <div>
          <Eyebrow>White label · invisible infrastructure</Eyebrow>
          <GradientHead className="mt-3">Your creators never know we&apos;re there.</GradientHead>
          <p className="mt-4 text-muted">
            Deploy CrewHQ as your own product. Your logo, your colors, your domain, your emails, your creator experience.
            We power the infrastructure. Your creators see only your brand.
          </p>
          <div className="mt-6 flex flex-col sm:flex-row gap-3">
            <button onClick={() => openLead("whitelabel_primary")} className="btn-primary text-sm inline-flex items-center gap-1.5" data-testid="wl-cta">
              Talk to Sales <ArrowRight size={12} weight="bold" />
            </button>
            <button onClick={() => openLead("whitelabel_secondary")} className="btn-ghost text-sm inline-flex items-center gap-1.5" data-testid="wl-cta-secondary">
              Request a demo <ArrowRight size={12} weight="bold" />
            </button>
          </div>
        </div>
        <div className="grid grid-cols-2 gap-3">
          {items.map((it, i) => {
            const Icon = it.icon;
            return (
              <div key={i} className={`glass p-5 hover-lift ${i === items.length - 1 ? "col-span-2" : ""}`} data-testid={`wl-${i}`}>
                <div className="w-9 h-9 rounded-lg bg-gradient-to-br from-[#8B5CF6] to-[#EC4899] grid place-items-center">
                  <Icon size={15} weight="bold" color="white" />
                </div>
                <div className="mt-3 text-white text-sm font-bold">{it.title}</div>
              </div>
            );
          })}
        </div>
      </Section>
    </div>
  );
}

// ---------- Section 8: Why Creator Operations ----------
function WhyCreatorOps() {
  const existing = [
    { title: "HR Operations",       body: "People, hiring, performance." },
    { title: "Finance Operations",  body: "Books, bills, reporting." },
    { title: "Sales Operations",    body: "Pipeline, quotas, forecasting." },
    { title: "Customer Operations", body: "Onboarding, support, success." },
  ];
  return (
    <div className="py-24 border-t border-white/5 relative overflow-hidden">
      <div className="absolute inset-0 -z-10">
        <div className="absolute -top-20 left-0 w-[500px] h-[500px] rounded-full bg-gradient-to-br from-[#A3E635]/10 to-transparent blur-3xl" />
      </div>
      <Section>
        <div className="max-w-3xl">
          <Eyebrow>The category</Eyebrow>
          <GradientHead className="mt-3">Every modern company already has ops functions.</GradientHead>
          <p className="mt-4 text-muted">
            HR. Finance. Sales. Customer. All operational disciplines with their own systems, workflows and software.
            Creator programs are next — and they need their own operating system.
          </p>
        </div>

        <div className="mt-10 grid sm:grid-cols-2 lg:grid-cols-4 gap-3">
          {existing.map((e, i) => (
            <div key={i} className="glass p-5" data-testid={`ops-${i}`}>
              <div className="text-white font-bold">{e.title}</div>
              <div className="mt-1 text-xs text-muted">{e.body}</div>
            </div>
          ))}
        </div>

        <div className="mt-6 glass p-6 lg:p-8 relative overflow-hidden ring-2 ring-[#22D3EE]/40" data-testid="ops-creator">
          <div className="absolute -top-16 -right-16 w-72 h-72 rounded-full bg-gradient-to-br from-[#22D3EE]/25 to-[#A3E635]/15 blur-3xl" />
          <div className="relative flex flex-col sm:flex-row items-start sm:items-center gap-5">
            <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-[#22D3EE] to-[#A3E635] grid place-items-center flex-shrink-0">
              <Sparkle size={22} weight="fill" color="white" />
            </div>
            <div className="flex-1">
              <div className="text-[10px] uppercase tracking-[0.22em] text-[#A3E635] font-bold">The next operational category</div>
              <div className="text-white text-2xl font-black mt-1">Creator Operations</div>
              <p className="mt-2 text-sm text-muted max-w-2xl">
                The discipline of running creator programs at scale — onboarding, campaigns, submissions,
                approvals, payouts, reporting. Powered by CrewHQ.
              </p>
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
}

// ---------- Section 9: Results ----------
function Results() {
  const outcomes = [
    { n: "72%",   l: "Reduction in approval cycle time" },
    { n: "5→0",   l: "Spreadsheets replaced per campaign" },
    { n: "100%",  l: "Creator data centralised in one place" },
    { n: "3.4x",  l: "Campaigns shipped per quarter" },
    { n: "83%",   l: "Manual workflows automated" },
    { n: "0",     l: "WhatsApp groups needed to ship a drop" },
  ];
  return (
    <div id="resources" className="py-24 border-t border-white/5">
      <Section>
        <div className="max-w-3xl">
          <Eyebrow>Operational outcomes</Eyebrow>
          <GradientHead className="mt-3">Not vanity metrics. Real ops improvements.</GradientHead>
          <p className="mt-4 text-muted">
            Impressions and reach aren&apos;t the problem. Operations are. Here&apos;s what teams achieve after moving to CrewHQ.
          </p>
        </div>
        <div className="mt-10 grid grid-cols-2 lg:grid-cols-3 gap-3">
          {outcomes.map((o, i) => (
            <div key={i} className="glass p-6" data-testid={`outcome-${i}`}>
              <div className="text-3xl sm:text-4xl font-black text-white tracking-tight">{o.n}</div>
              <div className="mt-1 text-xs uppercase tracking-wider text-muted font-bold">{o.l}</div>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}

// ---------- Section 10: Customer stories ----------
function CustomerStories() {
  const testimonials = [
    { q: "We replaced three WhatsApp groups, five spreadsheets and a manual approval chain with one workspace. Our creator program throughput 3x-ed the same quarter.",
      n: "Head of Creator Marketing", org: "D2C beauty brand" },
    { q: "Every brief, every submission, every payout — one queue, one status, one audit trail. Our ops lead got her Fridays back.",
      n: "Founder", org: "Talent agency (Mumbai)" },
    { q: "We used to lose creator data every time someone left. Now it lives in CrewHQ, forever. Onboarding a new campaign manager takes minutes.",
      n: "Program Lead", org: "Enterprise creator team" },
  ];
  const cases = [
    "From 40 to 2,400 active creators in two quarters — without hiring an ops manager.",
    "How an agency ran 18 concurrent client campaigns with a 3-person operations team.",
    "Cutting a 14-day approval cycle down to 36 hours across a 900-creator program.",
  ];
  return (
    <div className="py-24 border-t border-white/5">
      <Section>
        <div className="max-w-3xl">
          <Eyebrow>Customer stories</Eyebrow>
          <GradientHead className="mt-3">Operational wins, in their own words.</GradientHead>
        </div>
        <div className="mt-10 grid lg:grid-cols-3 gap-3">
          {testimonials.map((t, i) => (
            <div key={i} className="glass p-6" data-testid={`testimonial-${i}`}>
              <Quotes size={22} weight="fill" className="text-[#8B5CF6]" />
              <blockquote className="mt-3 text-white text-base leading-snug">{t.q}</blockquote>
              <div className="mt-5 text-xs uppercase tracking-wider text-muted font-bold">— {t.n} · {t.org}</div>
            </div>
          ))}
        </div>
        <div className="mt-10 grid sm:grid-cols-3 gap-3" data-testid="case-studies">
          {cases.map((c, i) => (
            <a key={i} href="#book-demo" className="glass p-5 hover-lift group" data-testid={`case-${i}`}>
              <div className="text-[10px] uppercase tracking-wider text-[#A3E635] font-bold">Case study</div>
              <div className="mt-1 text-white font-bold">{c}</div>
              <div className="mt-4 text-xs text-muted flex items-center gap-1.5">Read → <ArrowRight size={11} weight="bold" className="group-hover:translate-x-0.5 transition" /></div>
            </a>
          ))}
        </div>
      </Section>
    </div>
  );
}

// ---------- Section 11: Pricing ----------
function Pricing({ openLead }) {
  const tiers = [
    { name: "Launch", price: "Free during launch",
      body: "For teams starting out. All product features, unlocked while we launch.",
      features: [
        "Unlimited campaigns", "Unlimited creators", "All operational systems",
        "Reporting & analytics", "Email support",
      ] },
    { name: "Growth", featured: true, price: "Talk to sales",
      body: "For growing brands and agencies scaling to hundreds of creators.",
      features: [
        "Everything in Launch", "White label branding", "Custom domain",
        "Advanced automations", "Priority support",
      ] },
    { name: "Enterprise", price: "Custom",
      body: "For enterprise creator teams running 1,000+ creators.",
      features: [
        "Everything in Growth", "SSO & advanced security", "Dedicated CSM",
        "Custom SLAs", "Onboarding & migration",
      ] },
  ];
  return (
    <div id="pricing" className="py-24 border-t border-white/5">
      <Section>
        <div className="max-w-2xl">
          <Eyebrow>Pricing</Eyebrow>
          <GradientHead className="mt-3">Free while we launch. Simple as you scale.</GradientHead>
          <p className="mt-4 text-muted">Start with Launch pricing today. Move to Growth when you white-label. Move to Enterprise when your program hits four-digit creator counts.</p>
        </div>
        <div className="mt-10 grid lg:grid-cols-3 gap-3">
          {tiers.map((t, i) => (
            <div key={i}
                 className={`glass p-6 relative ${t.featured ? "ring-2 ring-[#22D3EE] shadow-deep" : ""}`}
                 data-testid={`price-${t.name.toLowerCase().replace(/\s+/g, "-")}`}>
              {t.featured && <div className="absolute -top-3 left-6 text-[10px] uppercase tracking-wider font-black bg-[#22D3EE] text-black px-2 py-0.5 rounded-full">Most popular</div>}
              <div className="text-white font-black text-xl">{t.name}</div>
              <div className="text-sm text-muted mt-1">{t.price}</div>
              <div className="text-[11px] text-muted mt-2 leading-relaxed">{t.body}</div>
              <ul className="mt-5 space-y-2 text-sm text-white/90">
                {t.features.map((f, j) => (
                  <li key={j} className="flex items-start gap-2"><CheckCircle size={14} weight="fill" className="text-[#A3E635] mt-0.5 flex-shrink-0" /> {f}</li>
                ))}
              </ul>
              <button onClick={() => openLead(`pricing_${t.name.toLowerCase()}`)}
                      className={`mt-6 inline-flex items-center gap-1.5 text-sm font-bold ${t.featured ? "text-[#22D3EE]" : "text-white"}`}
                      data-testid={`price-cta-${t.name.toLowerCase()}`}>
                Talk to Sales <ArrowRight size={12} weight="bold" />
              </button>
            </div>
          ))}
        </div>
      </Section>
    </div>
  );
}

// ---------- Final CTA ----------
function FinalCTA({ openLead }) {
  return (
    <div id="book-demo" className="py-24 border-t border-white/5">
      <Section>
        <div className="glass p-8 sm:p-12 relative overflow-hidden text-center">
          <div className="absolute -top-24 left-1/2 -translate-x-1/2 w-[600px] h-[600px] rounded-full bg-gradient-to-br from-[#8B5CF6]/25 to-[#22D3EE]/10 blur-3xl" />
          <div className="relative">
            <Eyebrow>Ready when you are</Eyebrow>
            <GradientHead className="mt-3">Run Your Creator Operations on CrewHQ.</GradientHead>
            <p className="mt-4 max-w-2xl mx-auto text-muted">
              The Operating System for Creator Operations. 30-minute walkthrough with real data, real workflows, no decks.
            </p>
            <div className="mt-8 flex flex-col sm:flex-row gap-3 justify-center">
              <button onClick={() => openLead("final_cta_primary")} className="btn-primary text-base px-6 py-3 flex items-center justify-center gap-2" data-testid="cta-talk-to-sales">
                Talk to Sales <ArrowRight size={14} weight="bold" />
              </button>
              <button onClick={() => openLead("final_cta_demo")} className="btn-ghost text-base px-6 py-3 flex items-center justify-center gap-2" data-testid="cta-book-demo">
                <Play size={14} weight="fill" /> Book Demo
              </button>
            </div>
          </div>
        </div>
      </Section>
    </div>
  );
}

// ---------- Footer ----------
function Footer({ openLead }) {
  return (
    <footer id="about" className="border-t border-white/5 pt-14 pb-10">
      <Section>
        <div className="grid sm:grid-cols-4 gap-8">
          <div className="sm:col-span-2">
            <div className="flex items-center gap-2">
              <div className="w-9 h-9 rounded-xl bg-gradient-to-br from-[#8B5CF6] to-[#EC4899] grid place-items-center">
                <Sparkle size={16} weight="fill" color="white" />
              </div>
              <div className="text-white font-black tracking-tight">Crew<span className="text-[#22D3EE]">HQ</span></div>
            </div>
            <p className="mt-4 text-sm text-muted max-w-md">
              The Operating System for Creator Operations.
            </p>
            <div className="mt-4 text-sm text-muted flex flex-wrap gap-x-6 gap-y-1">
              <a href="https://www.crewhq.in" className="hover:text-white" data-testid="footer-website">www.crewhq.in</a>
              <a href="mailto:info@crewhq.in" className="hover:text-white" data-testid="footer-email">info@crewhq.in</a>
            </div>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-muted font-bold">Platform</div>
            <ul className="mt-3 space-y-2 text-sm text-white/80">
              <li><a href="#platform">Creator Lifecycle</a></li>
              <li><a href="#solutions">Solutions</a></li>
              <li><a href="#pricing">Pricing</a></li>
              <li><a href="#customers">Customers</a></li>
            </ul>
          </div>
          <div>
            <div className="text-xs uppercase tracking-wider text-muted font-bold">Company</div>
            <ul className="mt-3 space-y-2 text-sm text-white/80">
              <li><a href="mailto:info@crewhq.in">Contact</a></li>
              <li><button onClick={() => openLead("footer_demo")} className="hover:text-white">Book Demo</button></li>
              <li><button onClick={() => openLead("footer_sales")} className="hover:text-white">Talk to Sales</button></li>
            </ul>
          </div>
        </div>
        <div className="mt-10 pt-6 border-t border-white/5 text-xs text-muted flex flex-wrap justify-between gap-3">
          <div>© {new Date().getFullYear()} CrewHQ. The Operating System for Creator Operations.</div>
          <div>Made for brands & agencies</div>
        </div>
      </Section>
    </footer>
  );
}

// ---------- Page ----------
export default function LandingPage() {
  const [leadOpen, setLeadOpen] = useState(false);
  const [leadSource, setLeadSource] = useState("landing_nav");
  const openLead = useCallback((source = "landing") => {
    setLeadSource(source); setLeadOpen(true);
  }, []);
  return (
    <div className="landing-light min-h-screen" data-testid="landing-page">
      <TopNav openLead={openLead} />
      <Hero openLead={openLead} />
      <Problem />
      <Lifecycle />
      <CorePlatform />
      <Solutions openLead={openLead} />
      <WhoItsFor />
      <WhiteLabel openLead={openLead} />
      <WhyCreatorOps />
      <Results />
      <CustomerStories />
      <Pricing openLead={openLead} />
      <FinalCTA openLead={openLead} />
      <Footer openLead={openLead} />
      <LeadModal open={leadOpen} onClose={() => setLeadOpen(false)} source={leadSource} />
    </div>
  );
}
